import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ICustomProductOptionConfig } from "@evennt-tech/api-v2-core";
import { customProductOptionConfigRepository } from "api/customProductOptionConfig";
import { FullProductWithShowValues } from "ts/api/product";

export const CUSTOM_CONFIG_ROWS_PAGE_SIZE = 50;

type ProductCustomConfigRows = {
  rows: FullProductWithShowValues[];
  // From the last loaded page; until then the product's summary count.
  total?: number;
  isLoading: boolean;
};

type LoadNextPageArgs = {
  productId: string;
  supplierCompanyId: string;
  showId: string;
  toRows: (
    configs: ICustomProductOptionConfig[]
  ) => FullProductWithShowValues[];
};

type State = {
  rowsByProductId: Record<string, ProductCustomConfigRows>;
  // Bumped by `reset`, so a page requested before it never lands after it.
  generation: number;
};

type Action = {
  loadNextPage: (args: LoadNextPageArgs) => Promise<void>;
  reset: () => void;
};

/**
 * The CUSTOM config rows of products whose matrix the supplier show list left
 * out, loaded a page at a time into expanded table rows.
 */
export const useShowProductCustomConfigRows = create(
  devtools<State & Action>((set, get) => ({
    rowsByProductId: {},
    generation: 0,

    loadNextPage: async ({ productId, supplierCompanyId, showId, toRows }) => {
      const { rowsByProductId, generation } = get();
      const current = rowsByProductId[productId];
      if (current?.isLoading) {
        return;
      }

      const loadedRows = current?.rows ?? [];
      const setProductRows = (productRows: ProductCustomConfigRows) => {
        if (get().generation !== generation) {
          return;
        }
        set((state) => ({
          rowsByProductId: {
            ...state.rowsByProductId,
            [productId]: productRows,
          },
        }));
      };

      setProductRows({ ...current, rows: loadedRows, isLoading: true });
      try {
        const {
          items,
          meta,
        } = await customProductOptionConfigRepository.getCustomConfigsPage({
          productId,
          supplierCompanyId,
          showId,
          page:
            Math.floor(loadedRows.length / CUSTOM_CONFIG_ROWS_PAGE_SIZE) + 1,
          limit: CUSTOM_CONFIG_ROWS_PAGE_SIZE,
        });
        const loadedIds = new Set(loadedRows.map(({ configId }) => configId));

        setProductRows({
          rows: [
            ...loadedRows,
            ...toRows(items).filter(({ configId }) => !loadedIds.has(configId)),
          ],
          total: meta.totalItems ?? current?.total,
          isLoading: false,
        });
      } catch (error) {
        setProductRows({ ...current, rows: loadedRows, isLoading: false });
        throw error;
      }
    },

    reset: () =>
      set((state) => ({
        rowsByProductId: {},
        generation: state.generation + 1,
      })),
  }))
);
